import type { ReactNode } from 'react';
import { Group, Text, Box } from '@mantine/core';

/** Ширина колонки заголовка (label) в InfoRow. */
const TITLE_WIDTH = 120;

interface InfoRowProps {
  children?: ReactNode;
  className?: string;
}

/**
 * InfoRow — строка с колонками label | value | (опционально description).
 * Используется внутри ViewPanel. Только layout и позиционирование.
 */
export function InfoRow({ children, className }: InfoRowProps) {
  return (
    <Group gap='md' align='center' wrap='nowrap' className={className}>
      {children}
    </Group>
  );
}

/**
 * TitleCol — колонка для label (метки). Фиксированная ширина, стили label.
 * Родитель передаёт текст или ReactNode.
 * @param width — переопределение ширины (по умолчанию TITLE_WIDTH = 90).
 */
export function TitleCol({ children, width }: { children?: ReactNode; width?: number }) {
  return (
    <Box style={{ width: width ?? TITLE_WIDTH, flexShrink: 0 }}>
      {typeof children === 'string' ? (
        <Text size='sm' fw={300}>
          {children}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
}

/**
 * ValueCol — колонка для основного значения. flex: 1, minWidth: 0 для корректного overflow.
 * display: flex + alignItems: flex-start — чтобы EditableText (и др.) был flex child и не «прыгал»
 * при переключении view→edit (в block baseline у span и input разный).
 */
export function ValueCol({ children }: { children?: ReactNode }) {
  return (
    <Box style={{ minWidth: 0, flex: 1, display: 'flex', alignItems: 'flex-start' }}>
      {children}
    </Box>
  );
}

/**
 * DescriptionCol — опциональная колонка для подсказки/доп. информации.
 * Меньший размер, приглушённый цвет.
 */
export function DescriptionCol({ children }: { children?: ReactNode }) {
  return (
    <Box style={{ flexShrink: 0 }}>
      {typeof children === 'string' ? (
        <Text size='xs' c='dimmed'>
          {children}
        </Text>
      ) : (
        children
      )}
    </Box>
  );
}

InfoRow.TitleCol = TitleCol;
InfoRow.ValueCol = ValueCol;
InfoRow.DescriptionCol = DescriptionCol;
